import type {
  DynamicElement,
  EditEntries,
  EditEntry,
  FieldWithAssignee,
  LocationDirection,
  SelectionAffinity,
} from '@aline/common';
import { AlineTransforms, Queries } from '@aline/common';
import { makeVar } from '@apollo/client';
import { isEmpty } from 'ramda';
import type { Location, NodeEntry, Point } from 'slate';
import { Editor, Path, Range } from 'slate';
import { getEditorTabStateForTabId } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import {
  EDITOR_TO_DOCUMENT_ELEMENT,
  EDITOR_TO_REACT_WINDOW,
} from 'src/layout-engine/components/Document';
import { EDITOR_TO_TAB_STATE } from 'src/layout-engine/utils/weak-maps';

type ScrollAffinity = 'first' | 'last';

type ScrollAlignment = 'auto' | 'center' | 'top';

/** The space to preserve above and below a line when scrolling it into view. */
const SCROLL_PADDING_PX = 4;

/** The minimum distance the mouse has to move vertically before we scroll towards it. */
const SCROLL_TO_MOUSE_MIN_DISTANCE = 10;

/** Factor determining how quickly we scroll in the direction of the mouse pointer. */
const SCROLL_TO_MOUSE_SPEED_FACTOR = 25;

/** The region at the top and bottom of the viewport in which we scroll towards the mouse. */
const SCROLL_TO_MOUSE_REGION = 50;

const suspendedSelectionScrollAtVar = makeVar<number | null>(null);

/**
 * Note: Scroll transforms don't require editingMode checks as any user in any editing mode can use these transforms.
 * The transforms, at most, only change the selection and don't alter the document structure.
 */
export const scrollTransforms = {
  /**
   * Scroll the editor to the next dynamic element assigned to the given user.
   */
  navigateToNextAssignedDynamicElement: (
    editor: Editor,
    options: {
      inputFields: FieldWithAssignee[];
      rWHeightPx: number;
      rWOffsetPx: number;
      userId: UUID;
      at?: Location;
      includeOnlyIncomplete?: boolean;
      match?: (n: DynamicElement) => boolean;
    }
  ): NodeEntry<DynamicElement> | void => {
    const { at = editor.selection, rWHeightPx, rWOffsetPx } = options;
    if (!at) return;

    const nextAssignedEntry = Queries.getNextAssignedDynamicElement(editor, options);
    if (!nextAssignedEntry) return;

    const [, nextMatchPath] = nextAssignedEntry;
    ClientTransforms.scrollToLocation(editor, {
      affinity: 'last',
      cMLocation: Editor.start(editor, nextMatchPath),
      rWHeightPx,
      rWOffsetPx,
    });

    return nextAssignedEntry;
  },

  /**
   * Scroll the editor to the next dynamic element *after* a location.
   * The default match simply finds the next dynamic element after the selection.
   */
  navigateToNextDynamicElement: (
    editor: Editor,
    options: {
      rWHeightPx: number;
      rWOffsetPx: number;
      at?: Location;
      direction?: LocationDirection;
      match?: (n: DynamicElement) => boolean;
      scrollAlignment?: ScrollAlignment;
      select?: boolean;
    }
  ): NodeEntry<DynamicElement> | void => {
    const {
      at = editor.selection,
      rWHeightPx,
      rWOffsetPx,
      scrollAlignment,
      select = true,
    } = options;
    if (!at) return;

    const nextMatchEntry = Queries.getNextDynamicElement(editor, { ...options, at });
    if (!nextMatchEntry) return;

    const [, nextMatchPath] = nextMatchEntry;
    ClientTransforms.scrollToLocation(editor, {
      affinity: 'last',
      cMLocation: Editor.start(editor, nextMatchPath),
      rWHeightPx,
      rWOffsetPx,
      scrollAlignment,
      select,
    });

    return nextMatchEntry;
  },

  /**
   * Scrolls a given point into view in the viewport.
   */
  scrollPointIntoView: (
    editor: Editor,
    options: {
      affinity: SelectionAffinity;
      cMPoint: Point;
      rWHeightPx: number;
      rWOffsetPx: number;
      scrollAlignment?: ScrollAlignment;
    }
  ): void => {
    const { affinity, cMPoint, rWHeightPx, rWOffsetPx, scrollAlignment = 'auto' } = options;

    const reactWindow = EDITOR_TO_REACT_WINDOW.get(editor);
    const minYTop = rWOffsetPx;
    const maxYBottom = rWOffsetPx + rWHeightPx;

    // If react-window's height is 0px, it hasn't been rendered correctly yet.
    if (!reactWindow || rWHeightPx === 0) return;

    /**
     * `'top'` alignment forces the point to the top of the viewport regardless
     * of whether it's already "on-screen" per react-window's math. Used on
     * mobile so the target lands above the bottom sheet — react-window's
     * viewport spans the full window there, so the default `'auto'` mode sees
     * fields hidden behind the sheet as visible and skips the scroll.
     */
    if (scrollAlignment === 'top') {
      const bounds = getFirstLineBounds(editor, cMPoint, affinity);
      if (!bounds) return;

      reactWindow.scrollTo(Math.max(0, bounds.yTop - SCROLL_PADDING_PX));
      return;
    }

    /**
     * `'center'` puts the line in the middle of the viewport even when it's
     * already on-screen. Citations and jump-to use this so the reader isn't
     * left looking at a line parked on the edge.
     */
    if (scrollAlignment === 'center') {
      const bounds = getFirstLineBounds(editor, cMPoint, affinity);
      if (!bounds) return;

      const lineCenter = (bounds.yTop + bounds.yBottom) / 2;
      reactWindow.scrollTo(Math.max(0, lineCenter - rWHeightPx / 2));
      return;
    }

    /**
     * The minimum distance we must scroll to bring some line containing the point entirely into
     * view.
     */
    let minCorrectionAmount = Infinity;

    for (const rMLineEntry of Queries.getRMLinesAboveCMPoint(editor, cMPoint, affinity)) {
      const [, rMLinePath] = rMLineEntry;

      const pageNum = Queries.getPageNumberForRMPath(editor, rMLinePath);
      if (pageNum === null) continue;

      const { yBottom: relativeYBottom, yTop: relativeYTop } = Queries.getRMNodeAbsolutePosition(
        editor,
        rMLineEntry
      );

      const pageTop = Queries.getTopOfPagePx(editor, pageNum);
      const yTop = pageTop + relativeYTop;
      const yBottom = pageTop + relativeYBottom;

      /**
       * The extent to which the top of the line is above the top of the viewport. A non-positive
       * value means that the top of the line is not above the top of the viewport.
       */
      const topOffScreenAmount = minYTop - yTop + SCROLL_PADDING_PX;

      /**
       * Likewise for the bottom of the line. Non-positive means that the bottom of the line is not
       * below the bottom of the viewport.
       */
      const bottomOffScreenAmount = yBottom - maxYBottom + SCROLL_PADDING_PX;

      // If at least one line is on-screen, do nothing
      const isOffScreen = topOffScreenAmount > 0 || bottomOffScreenAmount > 0;
      if (!isOffScreen) return;

      /**
       * The amount by which we must scroll to bring the line on-screen. Positive means down,
       * negative means up.
       */
      const correctionAmount =
        topOffScreenAmount > bottomOffScreenAmount ? -topOffScreenAmount : bottomOffScreenAmount;

      if (Math.abs(correctionAmount) < Math.abs(minCorrectionAmount)) {
        minCorrectionAmount = correctionAmount;
      }
    }

    if (minCorrectionAmount === Infinity) return;

    // Scroll the minimum required distance
    reactWindow.scrollTo(Math.max(0, rWOffsetPx + minCorrectionAmount));
  },

  scrollSelectionIntoView: (editor: Editor): void => {
    if (isSelectionScrollSuspended()) return;

    const reactWindowState = getReactWindowStateForEditor(editor);
    if (!reactWindowState) return;

    const { scrollOffset: rWOffsetPx, windowHeight: rWHeightPx } = reactWindowState;

    if (!editor.selection) return;

    // NOTE: This is a heuristic to make sure we don't scroll to a list marker
    // selection, since that's only a point at the start of the first list
    // item. Optimally in the future, we might want to scroll if none of the
    // selected list items are visible.
    if (editor.selection.listMarker) return;

    const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    if (!selBoundaryEntry) return;

    const [, selBoundaryPath] = selBoundaryEntry;
    const selBoundaryRange = Editor.range(editor, selBoundaryPath);

    /**
     * If the entire selection boundary is selected (e.g., in a select all operation), don't scroll anything.
     */
    if (Range.isExpanded(editor.selection) && Range.equals(editor.selection, selBoundaryRange))
      return;

    ClientTransforms.scrollPointIntoView(editor, {
      affinity: editor.selection.affinity ?? 'forward',
      cMPoint: editor.selection.focus,
      rWHeightPx,
      rWOffsetPx,
    });
  },

  /**
   * Utility function for scrolling to an edit entry.
   */
  scrollToChange: (
    editor: Editor,
    options: { editEntry: EditEntry; rWHeightPx: number; rWOffsetPx: number }
  ): void => {
    const { editEntry, rWHeightPx, rWOffsetPx } = options;

    switch (editEntry.edit.type) {
      case 'styleSheet':
      case 'pageDimensions':
      case 'pageFormat': {
        const [, documentPath] = Queries.getDocument(editor);
        ClientTransforms.scrollToLocation(editor, {
          cMLocation: Editor.start(editor, documentPath),
          rWHeightPx,
          rWOffsetPx,
        });

        break;
      }
      case 'headerFormat': {
        const headersEntry = Queries.getHeadersElement(editor);
        if (!headersEntry) return;

        const activeHeaders = Queries.activeHeaderTypes(editor);
        if (isEmpty(activeHeaders)) return;

        const [, headerPath] = Queries.getHeaderOrFooter({
          editor,
          partEntry: headersEntry,
          type: activeHeaders[0],
        });

        ClientTransforms.scrollToLocation(editor, {
          cMLocation: Editor.start(editor, headerPath),
          rWHeightPx,
          rWOffsetPx,
        });

        break;
      }
      case 'footerFormat': {
        const footersEntry = Queries.getFootersElement(editor);
        if (!footersEntry) return;

        const activeFooters = Queries.activeFooterTypes(editor);
        if (isEmpty(activeFooters)) return;

        const [, footerPath] = Queries.getHeaderOrFooter({
          editor,
          partEntry: footersEntry,
          type: activeFooters[0],
        });

        ClientTransforms.scrollToLocation(editor, {
          cMLocation: Editor.start(editor, footerPath),
          rWHeightPx,
          rWOffsetPx,
        });

        break;
      }
      default: {
        ClientTransforms.scrollToLocation(editor, {
          cMLocation: editEntry.range,
          rWHeightPx,
          rWOffsetPx,
        });

        break;
      }
    }
  },

  /**
   * Given a *content model location*, scrolls react-window to the *line* that corresponds to that location *in the render model*.
   * Affinity specifies a preference to pick a line if there is a location conflict.  For instance, in some cases, a point can
   * resolve to multiple lines (e.g., if the point is the end of a line but also the start of the next line).  Or, a *content model* path
   * can span multiple lines.  Affinity specifies which line to scroll to.
   */
  scrollToLocation: (
    editor: Editor,
    options: {
      cMLocation: Location;
      rWHeightPx: number;
      rWOffsetPx: number;
      affinity?: ScrollAffinity;
      scrollAlignment?: ScrollAlignment;
      select?: boolean;
    }
  ): void => {
    const {
      affinity = 'last',
      cMLocation,
      rWHeightPx,
      rWOffsetPx,
      scrollAlignment,
      select = true,
    } = options;

    const cMPoint: Point = Range.isRange(cMLocation)
      ? // Return the start of a range
        Range.start(cMLocation)
      : Path.isPath(cMLocation)
        ? // Return the start or end of a path, depending on affinity
          affinity === 'first'
          ? Editor.start(editor, cMLocation)
          : Editor.end(editor, cMLocation)
        : cMLocation;

    ClientTransforms.scrollPointIntoView(editor, {
      affinity: affinity === 'first' ? 'backward' : 'forward',
      cMPoint,
      rWHeightPx,
      rWOffsetPx,
      scrollAlignment,
    });
    /**
     * Selecting after a forced alignment would otherwise run the default
     * `'auto'` selection scroll and park the line on the viewport edge.
     */
    if (scrollAlignment === 'center' || scrollAlignment === 'top') suspendSelectionScroll();
    if (select) AlineTransforms.select(editor, cMLocation);
  },

  /**
   * Utility helper for finding the next change and scrolling the editor to that change.
   */
  scrollToNextChange: (
    editor: Editor,
    options: {
      editEntries: EditEntries;
      rWHeightPx: number;
      rWOffsetPx: number;
    }
  ): void => {
    const { editEntries, rWHeightPx, rWOffsetPx } = options;
    const nextChange = Queries.nextChange(editor, editEntries);
    if (!nextChange) return;

    ClientTransforms.scrollToChange(editor, {
      editEntry: nextChange,
      rWHeightPx,
      rWOffsetPx,
    });
  },

  /**
   * Utility helper for finding the prev change and scrolling the editor to that change.
   */
  scrollToPrevChange: (
    editor: Editor,
    options: {
      editEntries: EditEntries;
      rWHeightPx: number;
      rWOffsetPx: number;
    }
  ): void => {
    const { editEntries, rWHeightPx, rWOffsetPx } = options;
    const prevChange = Queries.prevChange(editor, editEntries);
    if (!prevChange) return;

    ClientTransforms.scrollToChange(editor, {
      editEntry: prevChange,
      rWHeightPx,
      rWOffsetPx,
    });
  },

  /**
   * If the mouse pointer is near the edge of the viewport, scroll in that direction.
   *
   * We use some additional rules to improve the UX:
   *
   * If the mouse pointer has moved only a small distance from its initial vertical position, do not
   * scroll. This makes the user less likely to scroll accidentally when selecting text near the
   * edge of the viewport.
   *
   * Scale the scrolling speed linearly according to the vertical position of the mouse pointer
   * relative to the point at which scrolling starts. This includes the error margin described
   * above, so that scrolling never starts abruptly when the user leaves this error margin.
   *
   * @returns {boolean} whether the page was scrolled
   */
  scrollTowardsMouse(
    editor: Editor,
    options: { clientY: number; deltaTimeMs: number; initialClientY: number }
  ): boolean {
    const { clientY, deltaTimeMs, initialClientY } = options;
    const reactWindowState = getReactWindowStateForEditor(editor);
    if (!reactWindowState) return false;

    const { scrollOffset } = reactWindowState;

    const reactWindow = EDITOR_TO_REACT_WINDOW.get(editor);
    const viewport = EDITOR_TO_DOCUMENT_ELEMENT.get(editor)?.getBoundingClientRect();
    if (!reactWindow || !viewport) return false;

    const scrollUpThreshold = Math.min(
      initialClientY - SCROLL_TO_MOUSE_MIN_DISTANCE,
      viewport.top + SCROLL_TO_MOUSE_REGION
    );

    const scrollDownThreshold = Math.max(
      initialClientY + SCROLL_TO_MOUSE_MIN_DISTANCE,
      viewport.bottom - SCROLL_TO_MOUSE_REGION
    );

    const scrollUpOffset = scrollUpThreshold - clientY;
    const scrollDownOffset = clientY - scrollDownThreshold;

    const withinScrollRegion = scrollUpOffset > 0 || scrollDownOffset > 0;
    if (!withinScrollRegion) return false;

    const scrollRegionOffset =
      scrollUpOffset > scrollDownOffset ? -scrollUpOffset : scrollDownOffset;

    const scrollSpeed = scrollRegionOffset * SCROLL_TO_MOUSE_SPEED_FACTOR;
    reactWindow.scrollTo(scrollOffset + (scrollSpeed * deltaTimeMs) / 1000);

    return true;
  },
};

export function isSelectionScrollSuspended() {
  const suspendedAt = suspendedSelectionScrollAtVar();
  if (suspendedAt === null) return false;
  return performance.now() - suspendedAt < 100;
}

export function suspendSelectionScroll() {
  suspendedSelectionScrollAtVar(performance.now());
}

function getReactWindowStateForEditor(editor: Editor) {
  const tabId = EDITOR_TO_TAB_STATE.get(editor)?.tabId ?? null;
  return getEditorTabStateForTabId(tabId)?.reactWindow ?? null;
}

/** The first render-model line that contains `cMPoint`, in document coordinates. */
function getFirstLineBounds(
  editor: Editor,
  cMPoint: Point,
  affinity: SelectionAffinity
): { yBottom: number; yTop: number } | null {
  let firstYBottom = Infinity;
  let firstYTop = Infinity;
  for (const rMLineEntry of Queries.getRMLinesAboveCMPoint(editor, cMPoint, affinity)) {
    const [, rMLinePath] = rMLineEntry;
    const pageNum = Queries.getPageNumberForRMPath(editor, rMLinePath);
    if (pageNum === null) continue;

    const { yBottom: relativeYBottom, yTop: relativeYTop } = Queries.getRMNodeAbsolutePosition(
      editor,
      rMLineEntry
    );
    const pageTop = Queries.getTopOfPagePx(editor, pageNum);
    const yTop = pageTop + relativeYTop;
    if (yTop >= firstYTop) continue;
    firstYTop = yTop;
    firstYBottom = pageTop + relativeYBottom;
  }
  if (firstYTop === Infinity) return null;
  return { yBottom: firstYBottom, yTop: firstYTop };
}
